import { APP_VERSION } from "../build-version";
import { isSurfaceEnabled } from "./features";
import { useT, type Locale } from "./i18n";
import { useRoute, type RouteKind } from "./useRoute";

interface FootCopy {
  region: string;
  references: string;
  made: string;
  coffee: string;
  links: Record<
    "faq" | "integrations" | "roadmap" | "about" | "impressum" | "datenschutz",
    string
  >;
}

/** Footer chrome, per locale. The reference links read the same as the view they
 *  open (integrations to Kanäle). Impressum and Datenschutz keep their German
 *  names: they are German legal pages by law. The one brand name left
 *  (Ambos Venture Lab) and the version stay as-is. */
const COPY: Record<Locale, FootCopy> = {
  en: {
    region: "Site footer",
    references: "References",
    made: "Made in Germany",
    coffee: "Buy me a coffee",
    links: {
      faq: "FAQ",
      integrations: "Channels",
      roadmap: "Roadmap",
      about: "About",
      impressum: "Impressum",
      datenschutz: "Privacy",
    },
  },
  de: {
    region: "Fußzeile",
    references: "Verweise",
    made: "Hergestellt in Deutschland",
    coffee: "Spendier mir einen Kaffee",
    links: {
      faq: "FAQ",
      integrations: "Kanäle",
      roadmap: "Roadmap",
      about: "Über",
      impressum: "Impressum",
      datenschutz: "Datenschutz",
    },
  },
};

/**
 * The footer carries ONLY what the masthead does not.
 *
 * It used to repeat the four work modes and the field guide, so every one of
 * `NAV` plus Triage appeared twice on the same page. That is not a second path
 * to anything: `NAV` is rendered on every route, and at 768px and below, where
 * next.css hides it, `MobileNavSheet` renders the very same list. The duplicate
 * row was noise in the one band of chrome that should read as a colophon, so
 * the modes and `guide` are gone and only surfaces the masthead never offers
 * are left. Keep it that way: adding a `NAV` kind back here re-creates the
 * duplication, and `AppFooter.test.tsx` fails if one does.
 */
const LINK_ORDER: (keyof FootCopy["links"] & RouteKind)[] = [
  "faq",
  "integrations",
  "roadmap",
  "about",
  "impressum",
  "datenschutz",
];

/**
 * Colophon. A slim mono band, a brand line on the
 * left, references on the right. The German tricolour is the one place a spot
 * of brand colour is allowed; kept small so it sits, it doesn't shout.
 */
export function AppFooter() {
  const { go } = useRoute();
  const { locale } = useT();
  const c = COPY[locale] ?? COPY.en;
  return (
    <footer className="nx-foot" aria-label={c.region}>
      <div className="nx-foot__brand">
        <span className="nx-foot__flag" aria-hidden="true">
          <span style={{ background: "#16110b" }} />
          <span style={{ background: "#d7141a" }} />
          <span style={{ background: "#f6c500" }} />
        </span>
        <span className="nx-foot__made">{c.made}</span>
        {/* kosche.wtf sat here and is withheld for now, at the owner's request.
         *  Deleted, not hidden with CSS: a `display: none` anchor is still in
         *  the DOM and still ships the URL in the bundle, so "hidden" would be
         *  a statement about the stylesheet rather than about the page. This is
         *  reversible in one edit: re-add an anchor in the shape of the two
         *  below. `AppFooter.test.tsx` asserts the href is absent, so it cannot
         *  come back by accident. */}
        <a
          className="nx-foot__link"
          href="https://ja-developer.de"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ambos Venture Lab
        </a>
        <a
          className="nx-foot__link"
          href="https://buymeacoffee.com/ambosdevelopment"
          target="_blank"
          rel="noopener noreferrer"
        >
          {c.coffee}
        </a>
      </div>
      <nav className="nx-foot__links" aria-label={c.references}>
        {LINK_ORDER.filter(isSurfaceEnabled).map((kind) => (
          <button key={kind} type="button" onClick={() => go(kind)}>
            {c.links[kind]}
          </button>
        ))}
        <span className="nx-foot__dim">v{APP_VERSION}</span>
      </nav>
    </footer>
  );
}
